import { useState, useEffect } from 'react';

/**
 * Custom hook that debounces a value
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up the timeout
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timeout if value changes (or component unmounts)
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Custom hook for debounced input handling
 * @param initialValue - Initial value for the input
 * @param onDebouncedChange - Callback when the debounced value changes
 * @param delay - Debounce delay in milliseconds
 */
export function useDebouncedInput<T = string>(
  initialValue: T,
  onDebouncedChange: (value: T) => void,
  delay: number = 500
) {
  const [value, setValue] = useState<T>(initialValue);
  const [isTyping, setIsTyping] = useState(false);
  const debouncedValue = useDebounce(value, delay);

  // Update local value when initial value changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Call the callback when debounced value changes
  useEffect(() => {
    if (debouncedValue !== initialValue) {
      onDebouncedChange(debouncedValue);
      setIsTyping(false);
    }
  }, [debouncedValue, initialValue, onDebouncedChange]);

  const handleChange = (newValue: T) => {
    setValue(newValue);
    setIsTyping(true);
  };

  return {
    value,
    setValue: handleChange,
    isTyping,
    debouncedValue
  };
}